import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataRefresh } from '../contexts/DataSyncContext';
import {
  Box, Paper, Typography, Button, IconButton, Avatar, Chip,
  ToggleButton, ToggleButtonGroup, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Snackbar, Alert, Divider, TextField, Badge, Grid, Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
  Today as TodayIcon, ViewDay as ViewDayIcon, ViewWeek as ViewWeekIcon,
  CalendarMonth as ViewMonthIcon,
  Refresh as RefreshIcon, AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon, Schedule as ScheduleIcon, Close as CloseIcon,
  PushPin as PinIcon, PushPinOutlined as PinOutlinedIcon,
  CheckCircle as DoneIcon, Assignment as UnassignedIcon,
  Search as SearchIcon, DragIndicator as DragIcon,
  OpenInNew as OpenIcon, Tune as TuneIcon,
} from '@mui/icons-material';
import {
  format, addDays, subDays, addMonths, subMonths, startOfWeek, startOfMonth,
  parseISO, isToday as isTodayFn, differenceInMinutes,
  isSameDay, isSameMonth, getDay, getDaysInMonth,
} from 'date-fns';
import api from '../services/api';
import { useWSRefresh } from '../contexts/WebSocketContext';

const HOUR_HEIGHT = 72;
const MIN_HEIGHT = HOUR_HEIGHT / 60;
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 20;
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR;
const SIDEBAR_WIDTH = 300;
const priorityColors: Record<string, string> = { low: '#4caf50', medium: '#2196f3', high: '#ff9800', urgent: '#f44336' };
const statusColors: Record<string, string> = {
  new: '#9e9e9e', unassigned: '#9e9e9e', assigned: '#2196f3', en_route: '#ff9800',
  on_site: '#9c27b0', in_progress: '#4caf50', pending_parts: '#ff5722',
  awaiting_parts: '#7c3aed', pending_review: '#f59e0b',
  completed: '#607d8b', cancelled: '#bdbdbd',
};
const TC = ['#14548C','#f093fb','#4facfe','#43e97b','#fa709a','#00bcd4','#ff7043','#ab47bc','#26a69a','#ef5350'];

interface CalEvent {
  id: number; ticketNumber: string; title: string; start: Date; end: Date;
  techId: number | null; techName: string; techColor: string; status: string;
  priority: string; customerName: string; address: string; estimatedMinutes: number;
  actualStart: Date | null; actualEnd: Date | null; pinnedStart: boolean; originalStart: Date | null;
  returnTrip: boolean; missed: boolean; aged: boolean;
}
interface Tech { id: number; name: string; initials: string; color: string; }
interface UJob { id: number; ticketNumber: string; title: string; priority: string; customerName: string; address: string; estimatedMinutes: number; }

const fmtTime = (d: Date) => format(d, 'h:mm a');
const topForTime = (d: Date) => ((d.getHours() - DAY_START_HOUR) * 60 + d.getMinutes()) * MIN_HEIGHT;
const heightForMin = (m: number) => Math.max(m * MIN_HEIGHT, 22);

export default function CalendarPage() {
  const navigate = useNavigate();
  const [curDate, setCurDate] = useState(new Date());
  const isMobile = useMediaQuery('(max-width:900px)');
  const [view, setView] = useState<'day'|'week'|'month'|'quarter'|'sixmonth'>(
    typeof window !== 'undefined' && window.innerWidth <= 900 ? 'day' : 'week');
  const [flagFilter, setFlagFilter] = useState<{missed:boolean;needs_return:boolean;aged:boolean}>({missed:false,needs_return:false,aged:false});
  const [agingDays, setAgingDays] = useState<number>(0);
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [selTechs, setSelTechs] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState<'success'|'warning'|'error'|'info'>('info');
  const [unassigned, setUnassigned] = useState<UJob[]>([]);
  const [uSearch, setUSearch] = useState('');
  const [sideTab, setSideTab] = useState<'unassigned'|'tech'>('unassigned');
  const [showCalAssigned, setShowCalAssigned] = useState(true);
  const [showCalUnassigned, setShowCalUnassigned] = useState(false);
  const [sideTech, setSideTech] = useState<number|''>('');
  const [sideOpen, setSideOpen] = useState(typeof window !== 'undefined' ? window.innerWidth > 900 : true);
  const [dragging, setDragging] = useState<CalEvent|UJob|null>(null);
  const [dragIsU, setDragIsU] = useState(false);
  const [dragSlot, setDragSlot] = useState<{date:Date;sk:number}|null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [pendDrop, setPendDrop] = useState<{jid:number;date:Date;h:number;m:number;title:string}|null>(null);
  const [assignTech, setAssignTech] = useState<number|''>('');
  const [detEv, setDetEv] = useState<CalEvent|null>(null);
  const [detOpen, setDetOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [armedJob, setArmedJob] = useState<UJob|null>(null);
  // Touch / pointer drag state (HTML5 DnD does not fire on touch devices)
  const pdRef = useRef<any>({active:false,moved:false,suppress:false});
  const slotRef = useRef<{date:Date;sk:number}|null>(null);
  // Bigger hour rows on phones so 15-min slots stay tappable
  const HH = isMobile ? 96 : HOUR_HEIGHT;
  const MH = HH/60;
  const topFor = (d:Date)=>((d.getHours()-DAY_START_HOUR)*60+d.getMinutes())*MH;
  const hFor = (m:number)=>Math.max(m*MH,24);

  useEffect(()=>{(async()=>{
    try{const r=await api.get('/technicians');const d=r.data||[];
      const m:Tech[]=d.map((t:any,i:number)=>({id:t.id,name:t.full_name||`${t.first_name||''} ${t.last_name||''}`.trim(),
        initials:`${(t.first_name||'')[0]||''}${(t.last_name||'')[0]||''}`.toUpperCase(),color:TC[i%TC.length]}));
      setTechs(m);setSelTechs(m.map(t=>t.id));
    }catch{try{const r=await api.get('/tracking/locations/live');const d=r.data?.technicians||r.data||[];
      const m:Tech[]=d.map((t:any,i:number)=>({id:t.id,name:t.name,
        initials:t.initials||t.name?.split(' ').map((w:string)=>w[0]).join('').slice(0,2).toUpperCase()||'??',
        color:t.color||TC[i%TC.length]}));
      setTechs(m);setSelTechs(m.map(t=>t.id));}catch{}}
  })();},[]);

  const fetchEvents = useCallback(async(silent=false)=>{
    if(!silent)setLoading(true);
    try{const r=await api.get('/tickets',{params:{status:'assigned,en_route,on_site,in_progress,completed',page_size:500}});
      const tix=r.data?.items||r.data?.tickets||r.data||[];const mapped:CalEvent[]=[];
      for(const t of tix){if(!t.scheduled_start||!t.assigned_to_id)continue;
        const start=parseISO(t.scheduled_start);const em=t.estimated_duration_minutes||60;
        let end:Date;if(t.actual_start&&t.actual_end)end=parseISO(t.actual_end);
        else if(t.scheduled_end)end=parseISO(t.scheduled_end);else end=new Date(start.getTime()+em*60000);
        const tc=techs.find(x=>x.id===t.assigned_to_id);
        mapped.push({id:t.id,ticketNumber:t.ticket_number||`#${t.id}`,title:t.title||'Untitled',start,end,
          techId:t.assigned_to_id,techName:t.assigned_to_name||tc?.name||'Unknown',techColor:tc?.color||'#14548C',
          status:t.status||'assigned',priority:t.priority||'medium',customerName:t.customer_name||'',
          address:[t.service_address_line1,t.service_city].filter(Boolean).join(', '),estimatedMinutes:em,
          actualStart:t.actual_start?parseISO(t.actual_start):null,actualEnd:t.actual_end?parseISO(t.actual_end):null,
          pinnedStart:t.pinned_start||false,originalStart:t.original_scheduled_start?parseISO(t.original_scheduled_start):null,
          returnTrip:!!t.return_trip_required,missed:!!t.missed,aged:!!t.aged});}
      setEvents(mapped);}catch(e){console.error('Fetch events failed',e);}finally{setLoading(false);}
  },[techs]);

  const fetchU = useCallback(async()=>{
    try{const[r1,r2]=await Promise.all([api.get('/tickets',{params:{status:'unassigned',page_size:100}}),
      api.get('/tickets',{params:{status:'new',page_size:100}})]);
      const all=[...(r1.data?.items||r1.data||[]),...(r2.data?.items||r2.data||[])];
      const seen=new Set<number>();const jobs:UJob[]=[];
      for(const t of all){if(seen.has(t.id))continue;seen.add(t.id);
        jobs.push({id:t.id,ticketNumber:t.ticket_number||`#${t.id}`,title:t.title||'Untitled',
          priority:t.priority||'medium',customerName:t.customer_name||'',
          address:[t.service_address_line1,t.service_city].filter(Boolean).join(', '),
          estimatedMinutes:t.estimated_duration_minutes||60});}
      const p:Record<string,number>={urgent:0,high:1,medium:2,low:3};
      jobs.sort((a,b)=>(p[a.priority]??2)-(p[b.priority]??2));setUnassigned(jobs);}catch{}
  },[]);

  useDataRefresh(['calendar','tickets'],()=>{fetchEvents(true);fetchU();});
  useEffect(()=>{if(techs.length){fetchEvents();fetchU();}
    const iv = setInterval(()=>{fetchEvents(true);fetchU();}, 30000);
    return ()=>clearInterval(iv);
  },[fetchEvents,fetchU,techs]);

  useEffect(()=>{ if(isMobile && (view==='quarter'||view==='sixmonth')) setView('month'); },[isMobile,view]);
  const goToday=()=>setCurDate(new Date());
  const goPrev=()=>setCurDate(d=>view==='sixmonth'?subMonths(d,6):view==='quarter'?subMonths(d,3):view==='month'?subMonths(d,1):view==='week'?subDays(d,7):subDays(d,1));
  const goNext=()=>setCurDate(d=>view==='sixmonth'?addMonths(d,6):view==='quarter'?addMonths(d,3):view==='month'?addMonths(d,1):view==='week'?addDays(d,7):addDays(d,1));
  const weekDates=useMemo(()=>{const s=startOfWeek(curDate);return Array.from({length:7},(_,i)=>addDays(s,i));},[curDate]);
  const dispDates=view==='week'?weekDates:view==='day'?[curDate]:[];
  const filtEv=events.filter(e=>{
    if(!showCalAssigned && e.techId) return false;
    if(e.techId && !selTechs.includes(e.techId)) return false;
    const anyFlag=flagFilter.missed||flagFilter.needs_return||flagFilter.aged;
    if(anyFlag && !((flagFilter.missed&&e.missed)||(flagFilter.needs_return&&e.returnTrip)||(flagFilter.aged&&e.aged))) return false;
    return true;
  });
  // Unassigned events for calendar (have scheduled_start but no tech)
  const unassignedEvts = showCalUnassigned ? unassigned.filter(j => {
    const q = uSearch.toLowerCase();
    return !q || j.title.toLowerCase().includes(q) || j.customerName.toLowerCase().includes(q);
  }) : [];
  void unassignedEvts; // reserved for calendar overlay
  const agingFiltEv = agingDays>0 ? filtEv.filter(e=>(Date.now()-e.start.getTime())/86400000>=agingDays && e.status!=='completed') : filtEv;
  const evForDay=(date:Date)=>agingFiltEv.filter(e=>isSameDay(e.start,date)).sort((a,b)=>a.start.getTime()-b.start.getTime());
  const filtU=useMemo(()=>{if(!uSearch)return unassigned;const q=uSearch.toLowerCase();
    return unassigned.filter(j=>j.title.toLowerCase().includes(q)||j.customerName.toLowerCase().includes(q)||j.ticketNumber.toLowerCase().includes(q));},[unassigned,uSearch]);

  const onUDragStart=(e:React.DragEvent,j:UJob)=>{setDragging(j);setDragIsU(true);e.dataTransfer.effectAllowed='move';e.dataTransfer.setData('text/plain',`u:${j.id}`);};
  const evPointerDown=(e:React.PointerEvent,ev:CalEvent)=>{
    if(ev.pinnedStart||ev.status==='completed')return;
    startPointerDrag(e,ev,false,ev.title);};
  const onEvDragStart=(e:React.DragEvent,ev:CalEvent)=>{
    if(ev.pinnedStart){e.preventDefault();showSnack('Unpin first to move.','warning');return;}
    if(ev.status==='completed'){e.preventDefault();showSnack('Cannot move completed.','warning');return;}
    setDragging(ev);setDragIsU(false);e.dataTransfer.effectAllowed='move';e.dataTransfer.setData('text/plain',`e:${ev.id}`);};
  const onDragOver=(e:React.DragEvent,date:Date,sk:number)=>{e.preventDefault();e.dataTransfer.dropEffect='move';setDragSlot({date,sk});};
  const onDragLeave=()=>setDragSlot(null);
  const performDrop=async(item:CalEvent|UJob,isU:boolean,dropDate:Date,sk:number)=>{
    const hrs=Math.floor(sk);const mins=Math.round((sk-hrs)*60);
    if(isU){setPendDrop({jid:item.id,date:dropDate,h:hrs,m:mins,title:(item as UJob).title});setAssignTech('');setAssignOpen(true);return;}
    const ev=item as CalEvent;const ns=new Date(dropDate);ns.setHours(hrs,mins,0,0);
    const ne=new Date(ns.getTime()+ev.estimatedMinutes*60000);
    const fmtISO=(d:Date)=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:00`;
    try{await api.patch(`/tickets/${ev.id}`,{scheduled_start:fmtISO(ns),scheduled_end:fmtISO(ne)});
      showSnack(`Moved "${ev.title}" to ${format(ns,'h:mm a, MMM d')}`,'success');await fetchEvents(true);
    }catch(err:any){showSnack(err.response?.data?.detail||'Move failed','error');}};
  const onDrop=async(e:React.DragEvent,dropDate:Date,sk:number)=>{
    e.preventDefault();setDragSlot(null);if(!dragging)return;
    const it=dragging;const isU=dragIsU;setDragging(null);setDragIsU(false);
    await performDrop(it,isU,dropDate,sk);};
  const onDragEnd=()=>{setDragging(null);setDragIsU(false);setDragSlot(null);};

  /* ---------- TOUCH / POINTER drag-and-drop ----------
     HTML5 drag events never fire on touch, so for pen/touch pointers we run a
     pointer-events drag: capture the pointer, follow it with a ghost, hit-test
     calendar slots with elementFromPoint, and drop on pointerup. Mouse pointers
     fall through to the native HTML5 DnD above (desktop unchanged).            */
  const setSlotFromPoint=(x:number,y:number)=>{
    const g=pdRef.current.ghost as HTMLElement|undefined;
    if(g)g.style.display='none';
    const el=document.elementFromPoint(x,y) as HTMLElement|null;
    if(g)g.style.display='block';
    const slotEl=el?el.closest('[data-slot]') as HTMLElement|null:null;
    if(slotEl&&slotEl.dataset.slot){
      const [ds,sks]=slotEl.dataset.slot.split('|');
      const nx={date:new Date(ds),sk:parseFloat(sks)};
      slotRef.current=nx;setDragSlot(nx);
    }else{slotRef.current=null;setDragSlot(null);}
  };
  const startPointerDrag=(e:React.PointerEvent,item:CalEvent|UJob,isU:boolean,label:string)=>{
    if(e.pointerType==='mouse')return; // desktop keeps native HTML5 DnD
    const el=e.currentTarget as HTMLElement;
    try{el.setPointerCapture(e.pointerId);}catch{}
    pdRef.current={active:true,moved:false,suppress:false,item,isU,label,
      startX:e.clientX,startY:e.clientY,ghost:undefined,el};
    setDragging(item);setDragIsU(isU);
  };
  const pointerMove=(e:React.PointerEvent)=>{
    const d=pdRef.current;if(!d.active)return;
    if(!d.moved&&Math.hypot(e.clientX-d.startX,e.clientY-d.startY)<6)return;
    if(!d.moved){
      d.moved=true;d.suppress=true;
      const g=document.createElement('div');
      g.textContent=d.label;
      g.style.cssText='position:fixed;z-index:2147483000;pointer-events:none;max-width:190px;'+
        'padding:6px 10px;border-radius:6px;background:#1976d2;color:#fff;font:600 12px sans-serif;'+
        'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;box-shadow:0 6px 18px rgba(0,0,0,.3)';
      document.body.appendChild(g);d.ghost=g;
    }
    e.preventDefault();
    d.ghost.style.left=(e.clientX+12)+'px';
    d.ghost.style.top=(e.clientY-34)+'px';
    setSlotFromPoint(e.clientX,e.clientY);
  };
  const endPointerDrag=()=>{
    const d=pdRef.current;
    if(d.ghost){try{d.ghost.remove();}catch{}}
    if(d.el){try{d.el.releasePointerCapture&&d.el.releasePointerCapture(d.pid);}catch{}}
    pdRef.current={active:false,moved:false,suppress:d.moved};
    setDragging(null);setDragIsU(false);setDragSlot(null);slotRef.current=null;
  };
  const pointerUp=async(e:React.PointerEvent)=>{
    const d=pdRef.current;if(!d.active)return;
    const moved=d.moved;const item=d.item;const isU=d.isU;
    if(moved)setSlotFromPoint(e.clientX,e.clientY);
    const slot=slotRef.current;
    endPointerDrag();
    if(moved&&slot&&item)await performDrop(item,isU,slot.date,slot.sk);
  };
  const pointerCancel=()=>{if(pdRef.current.active)endPointerDrag();};

  /* tap-to-assign: tap a job, then tap a slot (works at every width, essential on phones) */
  const slotTap=(date:Date,sk:number)=>{
    if(!armedJob)return;
    setPendDrop({jid:armedJob.id,date,h:Math.floor(sk),m:Math.round((sk-Math.floor(sk))*60),title:armedJob.title});
    setAssignTech('');setAssignOpen(true);setArmedJob(null);
  };

  const confirmAssign=async()=>{if(!pendDrop||!assignTech)return;
    const{jid,date,h,m}=pendDrop;const ns=new Date(date);ns.setHours(h,m,0,0);
    const j=unassigned.find(x=>x.id===jid);const ne=new Date(ns.getTime()+(j?.estimatedMinutes||60)*60000);
    const fmtISO2=(d:Date)=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:00`;
    try{await api.post(`/tickets/${jid}/assign`,{assigned_to_id:assignTech,scheduled_start:fmtISO2(ns),scheduled_end:fmtISO2(ne)});
      const tc=techs.find(t=>t.id===assignTech);showSnack(`Assigned "${pendDrop.title}" to ${tc?.name||'tech'} at ${format(ns,'h:mm a')}`,'success');
      setAssignOpen(false);setPendDrop(null);await Promise.all([fetchEvents(),fetchU()]);
    }catch(err:any){showSnack(err.response?.data?.detail||'Assign failed','error');}};

  const togglePin=async(ev:CalEvent)=>{try{await api.patch(`/tickets/${ev.id}`,{pinned_start:!ev.pinnedStart});
    showSnack(ev.pinnedStart?`Unpinned "${ev.title}"`:`Pinned "${ev.title}"`,'info');await fetchEvents();}catch{showSnack('Pin failed','error');}};
  const showSnack=(msg:string,sev:'success'|'warning'|'error'|'info'='info')=>{setSnackMsg(msg);setSnackSev(sev);};
  const openDet=(ev:CalEvent)=>{setDetEv(ev);setDetOpen(true);};

  const monthGrid=useMemo(()=>{if(view!=='month')return[];const f=startOfMonth(curDate);const sd=getDay(f);const dim=getDaysInMonth(curDate);
    const c:Date[]=[];for(let i=sd-1;i>=0;i--)c.push(subDays(f,i+1));for(let d=0;d<dim;d++)c.push(addDays(f,d));
    while(c.length<42)c.push(addDays(f,c.length-sd));return c;},[curDate,view]);
  const monthCells=(d:Date)=>{const f=startOfMonth(d);const sd=getDay(f);const dim=getDaysInMonth(d);const c:Date[]=[];for(let i=sd-1;i>=0;i--)c.push(subDays(f,i+1));for(let k=0;k<dim;k++)c.push(addDays(f,k));while(c.length<42)c.push(addDays(f,c.length-sd));return c;};


  // Auto-select tech in sidebar when single tech filtered
  useEffect(()=>{if(selTechs.length===1){setSideTech(selTechs[0]);setSideTab('tech');}else{setSideTech('');}},[selTechs]);

  // WebSocket push — instant refresh on any change
  useWSRefresh(['ticket:assigned', 'ticket:updated', 'ticket:status', 'ticket:created', 'ticket:deleted', 'schedule:updated'], () => { fetchEvents(true); fetchU(); });

  return (
    <Box sx={{height:'100%',display:'flex',flexDirection:'column',overflow:'hidden'}}>
      <Paper elevation={0} sx={{px:{xs:1,md:2},py:{xs:1,md:1.5},borderBottom:'1px solid',borderColor:'divider',display:'flex',alignItems:'center',gap:{xs:0.5,md:1.5},flexWrap:'wrap',flexShrink:0}}>
        <ScheduleIcon color="primary"/>{!isMobile&&<Typography variant="h6" fontWeight={700}>Calendar</Typography>}
        <IconButton size="small" onClick={goPrev}><ChevronLeftIcon/></IconButton>
        <Button size="small" variant={isTodayFn(curDate)?'contained':'outlined'} onClick={goToday} startIcon={<TodayIcon/>}>Today</Button>
        <IconButton size="small" onClick={goNext}><ChevronRightIcon/></IconButton>
        <Typography variant="subtitle1" fontWeight={600} sx={{minWidth:{xs:0,md:200},fontSize:{xs:12,md:16},flex:{xs:1,md:'none'}}}>
          {view==='sixmonth'?`${format(curDate,'MMM yyyy')} – ${format(addMonths(curDate,5),'MMM yyyy')}`:view==='quarter'?`${format(curDate,'MMM')} – ${format(addMonths(curDate,2),'MMM yyyy')}`:view==='month'?format(curDate,'MMMM yyyy'):view==='week'?`${format(weekDates[0],'MMM d')} – ${format(weekDates[6],'MMM d, yyyy')}`:format(curDate,'EEEE, MMM d, yyyy')}
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_,v)=>v&&setView(v)}>
          <ToggleButton value="day"><ViewDayIcon sx={{mr:0.5,fontSize:16}}/>Day</ToggleButton>
          <ToggleButton value="week"><ViewWeekIcon sx={{mr:0.5,fontSize:16}}/>Week</ToggleButton>
          <ToggleButton value="month"><ViewMonthIcon sx={{mr:0.5,fontSize:16}}/>Month</ToggleButton>
          {!isMobile&&<ToggleButton value="quarter"><ViewMonthIcon sx={{mr:0.5,fontSize:16}}/>Quarter</ToggleButton>}
          {!isMobile&&<ToggleButton value="sixmonth"><ViewMonthIcon sx={{mr:0.5,fontSize:16}}/>6-Month</ToggleButton>}
        </ToggleButtonGroup>
        {isMobile&&(<Tooltip title="Filters"><IconButton size="small" color={filtersOpen?'primary':'default'} onClick={()=>setFiltersOpen(o=>!o)}><TuneIcon/></IconButton></Tooltip>)}
        <Box sx={{display:(!isMobile||filtersOpen)?'flex':'none',gap:0.5}}>
          <Chip size="small" label="Missed" color={flagFilter.missed?'error':'default'} variant={flagFilter.missed?'filled':'outlined'} onClick={()=>setFlagFilter(f=>({...f,missed:!f.missed}))}/>
          <Chip size="small" label="Needs return" color={flagFilter.needs_return?'warning':'default'} variant={flagFilter.needs_return?'filled':'outlined'} onClick={()=>setFlagFilter(f=>({...f,needs_return:!f.needs_return}))}/>
          <Chip size="small" label="Aged" color={flagFilter.aged?'secondary':'default'} variant={flagFilter.aged?'filled':'outlined'} onClick={()=>setFlagFilter(f=>({...f,aged:!f.aged}))}/>
        </Box>
        <Box sx={{flex:1}}/>
        <Box sx={{display:(!isMobile||filtersOpen)?'flex':'none',gap:0.5,flexWrap:'wrap'}}>
          {techs.map(t=>(<Chip key={t.id} size="small" avatar={<Avatar sx={{bgcolor:t.color,width:20,height:20,fontSize:9}}>{t.initials}</Avatar>}
            label={t.name.split(' ')[0]} variant={selTechs.includes(t.id)?'filled':'outlined'}
            onClick={()=>setSelTechs(p=>p.includes(t.id)?p.filter(x=>x!==t.id):[...p,t.id])}
            sx={{opacity:selTechs.includes(t.id)?1:0.5,cursor:'pointer'}}/>))}
        </Box>
        <Tooltip title={sideOpen?'Hide jobs panel':'Show jobs panel'}>
          <IconButton size="small" onClick={()=>setSideOpen(!sideOpen)} color={sideOpen?'primary':'default'}>
            <Badge badgeContent={unassigned.length} color="error" max={99}><UnassignedIcon/></Badge>
          </IconButton>
        </Tooltip>
        <Box sx={{display:(!isMobile||filtersOpen)?'flex':'none',alignItems:'center',flexWrap:'wrap',gap:0.5}}>
          <FormControlLabel control={<Checkbox size="small" checked={showCalAssigned} onChange={e=>setShowCalAssigned(e.target.checked)} sx={{py:0}}/>}
            label={<Typography variant="caption">Assigned</Typography>} sx={{mr:0.5}}/>
          <FormControlLabel control={<Checkbox size="small" checked={showCalUnassigned} onChange={e=>setShowCalUnassigned(e.target.checked)} sx={{py:0}}/>}
            label={<Typography variant="caption">Unassigned</Typography>} sx={{mr:0.5}}/>
          <TextField size="small" type="number" label="Aging ≥ days" value={agingDays||''} onChange={e=>setAgingDays(parseInt(e.target.value)||0)} sx={{width:120,mr:0.5}} inputProps={{min:0}}/>
        </Box>
        <IconButton size="small" onClick={()=>{fetchEvents();fetchU();}} disabled={loading}>
          {loading?<CircularProgress size={18}/>:<RefreshIcon/>}
        </IconButton>
      </Paper>

      <Box sx={{flex:1,display:'flex',flexDirection:isMobile?'column':'row',overflow:'hidden',minHeight:0}}>
        {sideOpen&&(<Paper elevation={0} sx={{
          width:isMobile?'100%':SIDEBAR_WIDTH,minWidth:isMobile?0:SIDEBAR_WIDTH,
          height:isMobile?'36vh':'auto',flexShrink:0,
          borderRight:isMobile?'none':'1px solid',borderBottom:isMobile?'1px solid':'none',
          borderColor:'divider',display:'flex',flexDirection:'column',overflow:'hidden'}}>
          {/* Tab headers */}
          <Box sx={{display:'flex',borderBottom:'1px solid',borderColor:'divider'}}>
            <Box onClick={()=>setSideTab('unassigned')} sx={{flex:1,py:1,textAlign:'center',cursor:'pointer',fontWeight:700,fontSize:12,
              borderBottom:sideTab==='unassigned'?'2px solid #1976d2':'2px solid transparent',
              color:sideTab==='unassigned'?'primary.main':'text.secondary','&:hover':{bgcolor:'action.hover'}}}>
              <UnassignedIcon sx={{fontSize:14,mr:0.5,verticalAlign:'middle'}}/>Unassigned ({unassigned.length})
            </Box>
            <Box onClick={()=>setSideTab('tech')} sx={{flex:1,py:1,textAlign:'center',cursor:'pointer',fontWeight:700,fontSize:12,
              borderBottom:sideTab==='tech'?'2px solid #1976d2':'2px solid transparent',
              color:sideTab==='tech'?'primary.main':'text.secondary','&:hover':{bgcolor:'action.hover'}}}>
              <ScheduleIcon sx={{fontSize:14,mr:0.5,verticalAlign:'middle'}}/>Tech Jobs
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{px:1.5,py:0.75,borderBottom:'1px solid',borderColor:'divider'}}>
            <TextField size="small" fullWidth placeholder="Search jobs..." value={uSearch} onChange={e=>setUSearch(e.target.value)}
              InputProps={{startAdornment:<SearchIcon sx={{fontSize:16,mr:0.5,color:'text.disabled'}}/>}}
              sx={{'& .MuiInputBase-root':{height:30,fontSize:12}}}/>
          </Box>

          {/* Unassigned tab */}
          {sideTab==='unassigned'&&(
            <Box sx={{flex:1,overflow:'auto'}}>
              {filtU.length===0?(<Box sx={{p:3,textAlign:'center',color:'text.disabled'}}><Typography variant="body2">{unassigned.length===0?'No unassigned jobs':'No matches'}</Typography></Box>):(
                filtU.map(j=>(<Box key={j.id} draggable onDragStart={e=>onUDragStart(e,j)} onDragEnd={onDragEnd}
                  onClick={()=>{if(pdRef.current.suppress){pdRef.current.suppress=false;return;}setArmedJob(a=>(a&&a.id===j.id)?null:j);}}
                  sx={{px:1.5,py:1,borderBottom:'1px solid',borderColor:'divider',cursor:'grab','&:hover':{bgcolor:'action.hover'},
                    bgcolor:armedJob&&armedJob.id===j.id?'rgba(25,118,210,0.14)':'transparent',
                    borderLeft:`3px solid ${priorityColors[j.priority]||'#9e9e9e'}`}}>
                  <Box sx={{display:'flex',alignItems:'center',gap:0.5}}>
                    <Box onPointerDown={e=>startPointerDrag(e,j,true,j.title)} onPointerMove={pointerMove}
                      onPointerUp={pointerUp} onPointerCancel={pointerCancel}
                      sx={{display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                        width:{xs:28,md:18},height:{xs:32,md:18},ml:{xs:-0.5,md:0},touchAction:'none',cursor:'grab'}}>
                      <DragIcon sx={{fontSize:{xs:20,md:14},color:'text.disabled'}}/>
                    </Box>
                    <Typography variant="caption" fontWeight={700} noWrap sx={{flex:1,fontSize:12,cursor:'pointer','&:hover':{textDecoration:'underline'}}}
                      onClick={(e)=>{e.stopPropagation();navigate(`/tickets?id=${j.id}`);}}>{j.title}</Typography>
                    <Chip label={j.estimatedMinutes+'m'} size="small" sx={{height:18,fontSize:10,bgcolor:'action.selected'}}/>
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{pl:2.5,fontSize:11}}>{j.customerName}</Typography>
                  {j.address&&<Typography variant="caption" color="text.disabled" noWrap display="block" sx={{pl:2.5,fontSize:10}}>📍 {j.address}</Typography>}
                  <Box sx={{display:'flex',gap:0.5,pl:2.5,mt:0.25,alignItems:'center'}}>
                    <Chip label={j.ticketNumber} size="small" variant="outlined" sx={{height:16,fontSize:9,cursor:'pointer','&:hover':{bgcolor:'action.selected'}}}
                      onClick={()=>navigate(`/tickets?id=${j.id}`)}/>
                    <Chip label={j.priority} size="small" sx={{height:16,fontSize:9,bgcolor:priorityColors[j.priority],color:'white'}}/>
                  </Box>
                </Box>)))}</Box>)}

          {/* Tech Jobs tab */}
          {sideTab==='tech'&&(
            <Box sx={{flex:1,overflow:'auto',display:'flex',flexDirection:'column'}}>
              <Box sx={{px:1.5,py:1,borderBottom:'1px solid',borderColor:'divider'}}>
                <FormControl fullWidth size="small" sx={{'& .MuiInputBase-root':{height:32,fontSize:12}}}>
                  <InputLabel sx={{fontSize:12}}>Technician</InputLabel>
                  <Select value={sideTech} label="Technician" onChange={e=>setSideTech(e.target.value as number|'')}>
                    <MenuItem value=""><em>Select a technician</em></MenuItem>
                    {techs.map(t=>(<MenuItem key={t.id} value={t.id}><Box sx={{display:'flex',alignItems:'center',gap:1}}>
                      <Avatar sx={{width:20,height:20,fontSize:9,bgcolor:t.color}}>{t.initials}</Avatar>
                      <Typography variant="body2" fontSize={12}>{t.name}</Typography></Box></MenuItem>))}
                  </Select></FormControl>
              </Box>
              <Box sx={{flex:1,overflow:'auto'}}>
                {!sideTech?(<Box sx={{p:3,textAlign:'center',color:'text.disabled'}}><Typography variant="body2">Select a technician to see their jobs</Typography></Box>):(
                  (()=>{const techEvts=events.filter(e=>e.techId===sideTech).sort((a,b)=>a.start.getTime()-b.start.getTime());
                    const q=uSearch.toLowerCase();
                    const filtered=q?techEvts.filter(e=>e.title.toLowerCase().includes(q)||e.customerName.toLowerCase().includes(q)||e.ticketNumber.toLowerCase().includes(q)):techEvts;
                    const tc=techs.find(t=>t.id===sideTech);
                    return filtered.length===0?(<Box sx={{p:3,textAlign:'center',color:'text.disabled'}}>
                      <Typography variant="body2">{q?'No matches':`No jobs for ${tc?.name||'this tech'}`}</Typography></Box>):(
                      <>{filtered.map(ev=>{const done=ev.status==='completed';
                        return(<Box key={ev.id}
                          sx={{px:1.5,py:1,borderBottom:'1px solid',borderColor:'divider',
                          borderLeft:`3px solid ${done?statusColors.completed:ev.techColor}`,
                          bgcolor:done?'action.disabledBackground':'transparent',
                          '&:hover':{bgcolor:'action.hover'}}}>
                          <Box sx={{display:'flex',alignItems:'center',gap:0.5}}>
                            <Box sx={{width:8,height:8,borderRadius:'50%',bgcolor:statusColors[ev.status]||'#9e9e9e',flexShrink:0}}/>
                            <Typography variant="caption" fontWeight={700} noWrap sx={{flex:1,fontSize:12,cursor:'pointer','&:hover':{textDecoration:'underline'},
                              textDecoration:done?'line-through':'none'}}
                              onClick={()=>openDet(ev)}>{ev.returnTrip?'⚠ ':''}{ev.title}</Typography>
                            <Chip label={ev.status.replace('_',' ')} size="small" sx={{height:16,fontSize:9,bgcolor:statusColors[ev.status],color:'white'}}/>
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{pl:2,fontSize:11}}>{ev.customerName}</Typography>
                          <Box sx={{display:'flex',alignItems:'center',gap:0.5,pl:2,mt:0.25}}>
                            <AccessTimeIcon sx={{fontSize:11,color:'text.disabled'}}/>
                            <Typography variant="caption" color="text.secondary" sx={{fontSize:10}}>{fmtTime(ev.start)} \u2013 {fmtTime(ev.end)}</Typography>
                            <Chip label={`${differenceInMinutes(ev.end,ev.start)}m`} size="small" sx={{height:16,fontSize:9,ml:0.5}}/>
                            {ev.pinnedStart&&<PinIcon sx={{fontSize:11,color:'primary.main'}}/>}
                            <Box sx={{flex:1}}/>
                            <Tooltip title="Edit details"><IconButton size="small" onClick={()=>openDet(ev)} sx={{p:0.25}}>
                              <ScheduleIcon sx={{fontSize:12}}/></IconButton></Tooltip>
                            <Tooltip title="Open ticket"><IconButton size="small" onClick={()=>navigate(`/tickets?id=${ev.id}`)} sx={{p:0.25}}>
                              <OpenIcon sx={{fontSize:12}}/></IconButton></Tooltip>
                          </Box>
                          {ev.address&&<Typography variant="caption" color="text.disabled" noWrap display="block" sx={{pl:2,fontSize:10}}>📍 {ev.address}</Typography>}
                        </Box>);})}</>);})())}
              </Box>
            </Box>)}
        </Paper>)}

        <Box sx={{flex:1,minHeight:0,overflow:'hidden',display:'flex',flexDirection:'column'}}>
          {armedJob&&(
            <Box sx={{px:1.5,py:0.75,bgcolor:'primary.main',color:'#fff',display:'flex',alignItems:'center',gap:1,flexShrink:0}}>
              <Typography variant="caption" sx={{flex:1,fontWeight:600}} noWrap>Tap a time slot to schedule “{armedJob.title}”</Typography>
              <Button size="small" onClick={()=>setArmedJob(null)} sx={{color:'#fff',minWidth:0}}>Cancel</Button>
            </Box>)}
          {(view==='day'||view==='week')&&(
            <Box sx={{flex:1,overflow:'auto',display:'flex'}}>
              <Box sx={{width:52,flexShrink:0,borderRight:'1px solid',borderColor:'divider',bgcolor:'background.default'}}>
                <Box sx={{height:52,borderBottom:'1px solid',borderColor:'divider'}}/>
                {Array.from({length:TOTAL_HOURS},(_,i)=>i+DAY_START_HOUR).map(h=>(<Box key={h} sx={{height:HH,display:'flex',alignItems:'flex-start',justifyContent:'flex-end',pr:0.5,pt:0.25}}>
                  <Typography variant="caption" color="text.secondary" sx={{fontSize:10}}>{h>12?h-12:h}{h>=12?'p':'a'}</Typography></Box>))}
              </Box>
              {dispDates.map((date,di)=>{const de=evForDay(date);const isT=isTodayFn(date);return(
                <Box key={di} sx={{flex:1,minWidth:view==='day'?300:120,position:'relative',borderRight:'1px solid',borderColor:'divider'}}>
                  <Box sx={{position:'sticky',top:0,zIndex:10,textAlign:'center',py:0.5,borderBottom:'1px solid',borderColor:'divider',height:52,
                    bgcolor:isT?'primary.main':'background.paper',color:isT?'white':'text.primary',display:'flex',flexDirection:'column',justifyContent:'center'}}>
                    <Typography variant="caption" fontWeight={600} sx={{fontSize:10}}>{format(date,'EEE')}</Typography>
                    <Typography variant="h6" fontWeight={700} sx={{lineHeight:1}}>{format(date,'d')}</Typography>
                  </Box>
                  <Box sx={{position:'relative',height:TOTAL_HOURS*HH}}>
                    {Array.from({length:TOTAL_HOURS*4},(_,i)=>{const h=DAY_START_HOUR+Math.floor(i/4);const q=(i%4)*15;const sk=h+q/60;return(
                      <Box key={i} data-slot={`${date.toISOString()}|${sk}`}
                        onDragOver={e=>onDragOver(e,date,sk)} onDragLeave={onDragLeave} onDrop={e=>onDrop(e,date,sk)}
                        onClick={()=>slotTap(date,sk)}
                        sx={{height:HH/4,borderBottom:q===45?'1px solid':'1px dotted',borderColor:q===45?'divider':'rgba(0,0,0,0.05)',
                        cursor:armedJob?'copy':'default',
                        bgcolor:dragSlot&&isSameDay(dragSlot.date,date)&&dragSlot.sk===sk?'rgba(25,118,210,0.28)':(armedJob?'rgba(25,118,210,0.04)':'transparent')}}/>);})}
                    {isT&&(()=>{const now=new Date();const tp=topFor(now);if(tp<0||tp>TOTAL_HOURS*HH)return null;
                      return(<Box sx={{position:'absolute',top:tp,left:0,right:0,zIndex:5,borderTop:'2px solid #f44336',pointerEvents:'none'}}>
                        <Box sx={{width:8,height:8,bgcolor:'#f44336',borderRadius:'50%',mt:'-5px',ml:'-4px'}}/></Box>);})()}
                    {de.map(ev=>{const tp=topFor(ev.start);const dur=differenceInMinutes(ev.end,ev.start);const ht=hFor(dur);
                      const done=ev.status==='completed';const act=!!(ev.actualStart&&ev.actualEnd);
                      const ov=de.filter(o=>o.id!==ev.id&&o.start<ev.end&&o.end>ev.start);const tot=ov.length+1;
                      const si=ov.filter(o=>o.id<ev.id).length;const wp=100/tot;const lp=si*wp;
                      return(<Box key={ev.id} draggable={!ev.pinnedStart&&!done} onDragStart={e=>onEvDragStart(e,ev)} onDragEnd={onDragEnd}
                        onPointerDown={e=>evPointerDown(e,ev)} onPointerMove={pointerMove} onPointerUp={pointerUp} onPointerCancel={pointerCancel}
                        onClick={()=>{if(pdRef.current.suppress){pdRef.current.suppress=false;return;}openDet(ev);}}
                        sx={{position:'absolute',top:Math.max(tp,0),left:`calc(${lp}% + 2px)`,width:`calc(${wp}% - 4px)`,height:Math.max(ht,22),
                        touchAction:(!ev.pinnedStart&&!done)?'none':'auto',
                        bgcolor:done?'#e0e0e0':ev.techColor,color:done?'text.secondary':'white',borderRadius:0.5,
                        borderLeft:`3px solid ${done?statusColors.completed:priorityColors[ev.priority]||ev.techColor}`,
                        overflow:'hidden',cursor:ev.pinnedStart?'default':(done?'pointer':'grab'),
                        opacity:(dragging&&'techId' in dragging&&(dragging as CalEvent).id===ev.id&&!dragIsU)?0.3:(done?0.65:1),
                        pointerEvents:dragging&&!('techId' in dragging&&(dragging as CalEvent).id===ev.id)?'none':'auto',
                        '&:hover':{boxShadow:3,zIndex:20},zIndex:10,px:0.5,py:0.25}}>
                        {ev.pinnedStart&&<PinIcon sx={{position:'absolute',top:0,right:0,fontSize:10,opacity:0.8}}/>}
                        <Typography variant="caption" fontWeight={700} noWrap display="block" sx={{fontSize:10,lineHeight:1.2}}>{ev.returnTrip?'⚠ ':''}{ev.title}</Typography>
                        {ht>28&&<Typography variant="caption" noWrap display="block" sx={{fontSize:9,opacity:0.9}}>{ev.customerName}</Typography>}
                        {ht>40&&<Typography variant="caption" display="block" sx={{fontSize:9,opacity:0.85}}>{fmtTime(ev.start)}–{fmtTime(ev.end)}{act&&' ✓'}</Typography>}
                        {ht>55&&ev.originalStart&&ev.originalStart.getTime()!==ev.start.getTime()&&(
                          <Typography variant="caption" display="block" sx={{fontSize:8,opacity:0.6,textDecoration:'line-through'}}>was {fmtTime(ev.originalStart)}</Typography>)}
                        {done&&<DoneIcon sx={{position:'absolute',bottom:1,right:1,fontSize:12,color:'success.main'}}/>}
                      </Box>);})}
                  </Box>
                </Box>);})}
            </Box>)}

{(view==='quarter'||view==='sixmonth')&&(
            <Box sx={{p:1,overflow:'auto'}}>
              <Grid container spacing={1}>
                {Array.from({length:view==='quarter'?3:6},(_,mi)=>{const md=addMonths(curDate,mi);const cells=monthCells(md);return(
                  <Grid item xs={12} sm={6} md={4} key={mi}>
                    <Box sx={{border:'1px solid',borderColor:'divider',borderRadius:1,p:1}}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{mb:0.5}}>{format(md,'MMMM yyyy')}</Typography>
                      <Grid container>{['S','M','T','W','T','F','S'].map((d,i)=>(<Grid item xs key={i} sx={{textAlign:'center'}}><Typography variant="caption" color="text.secondary" sx={{fontSize:9}}>{d}</Typography></Grid>))}</Grid>
                      {Array.from({length:6},(_,wk)=>(<Grid container key={wk}>
                        {cells.slice(wk*7,wk*7+7).map((date,i)=>{const inM=isSameMonth(date,md);const de=evForDay(date);const isT=isTodayFn(date);const overdue=de.some(e=>(Date.now()-e.start.getTime())/86400000>=15&&e.status!=='completed');return(
                          <Grid item xs key={i} onClick={()=>{setCurDate(date);setView('day');}}
                            sx={{textAlign:'center',py:0.25,cursor:'pointer',borderRadius:0.5,opacity:inM?1:0.3,bgcolor:isT?'rgba(25,118,210,0.08)':'transparent','&:hover':{bgcolor:'action.hover'}}}>
                            <Typography variant="caption" sx={{fontSize:10,fontWeight:isT?800:400}}>{format(date,'d')}</Typography>
                            {de.length>0&&<Box sx={{height:5,width:5,borderRadius:'50%',bgcolor:overdue?'error.main':'primary.main',mx:'auto'}}/>}
                          </Grid>);})}
                      </Grid>))}
                    </Box>
                  </Grid>);})}
              </Grid>
            </Box>)}
          {view==='month'&&(
            <Box sx={{flex:1,overflow:'auto',p:1}}>
              <Grid container>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
                <Grid item xs key={d} sx={{textAlign:'center',py:0.5}}><Typography variant="caption" fontWeight={700} color="text.secondary">{d}</Typography></Grid>))}</Grid>
              {Array.from({length:6},(_,wk)=>(<Grid container key={wk} sx={{minHeight:100}}>
                {monthGrid.slice(wk*7,wk*7+7).map((date,i)=>{const isTM=isSameMonth(date,curDate);const isT=isTodayFn(date);const de=evForDay(date);
                  return(<Grid item xs key={i} data-slot={`${date.toISOString()}|9`}
                    onDragOver={e=>onDragOver(e,date,9)} onDragLeave={onDragLeave} onDrop={e=>onDrop(e,date,9)}
                    onClick={()=>slotTap(date,9)}
                    sx={{border:'1px solid',borderColor:'divider',p:0.5,cursor:armedJob?'copy':'default',minHeight:{xs:56,md:'auto'},
                    bgcolor:dragSlot&&isSameDay(dragSlot.date,date)?'rgba(25,118,210,0.08)':isT?'rgba(25,118,210,0.05)':(isTM?'background.paper':'action.disabledBackground'),
                    opacity:isTM?1:0.4}}>
                    <Typography variant="caption" fontWeight={isT?800:400} sx={{color:isT?'primary.main':'text.primary',fontSize:11}}>{format(date,'d')}</Typography>
                    {de.slice(0,4).map(ev=>(<Box key={ev.id} onClick={()=>openDet(ev)}
                      sx={{px:0.5,py:0.1,mb:0.25,borderRadius:0.5,bgcolor:ev.status==='completed'?'#e0e0e0':ev.techColor,
                      color:ev.status==='completed'?'text.secondary':'white',cursor:'pointer','&:hover':{opacity:0.85}}}>
                      <Typography variant="caption" noWrap sx={{fontSize:9,fontWeight:600}}>{ev.returnTrip?'⚠ ':''}{fmtTime(ev.start)} {ev.title}</Typography></Box>))}
                    {de.length>4&&<Typography variant="caption" color="text.secondary" sx={{fontSize:9,pl:0.5}}>+{de.length-4} more</Typography>}
                  </Grid>);})}</Grid>))}
            </Box>)}
        </Box>
      </Box>

      <Dialog open={assignOpen} onClose={()=>setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign to Technician</DialogTitle>
        <DialogContent>
          {pendDrop&&(<Box sx={{mb:2}}><Typography variant="body2" fontWeight={600}>{pendDrop.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {format(pendDrop.date,'MMM d, yyyy')} at {pendDrop.h>12?pendDrop.h-12:pendDrop.h}:{String(pendDrop.m).padStart(2,'0')} {pendDrop.h>=12?'PM':'AM'}
            </Typography></Box>)}
          <FormControl fullWidth sx={{mt:1}}><InputLabel>Select Technician</InputLabel>
            <Select value={assignTech} label="Select Technician" onChange={e=>setAssignTech(e.target.value as number)}>
              {techs.map(t=>(<MenuItem key={t.id} value={t.id}><Box sx={{display:'flex',alignItems:'center',gap:1}}>
                <Avatar sx={{width:28,height:28,fontSize:11,bgcolor:t.color}}>{t.initials}</Avatar><Typography>{t.name}</Typography></Box></MenuItem>))}
            </Select></FormControl>
          <Box sx={{mt:2,display:'flex',gap:1,flexWrap:'wrap'}}>
            {techs.map(t=>(<Chip key={t.id} avatar={<Avatar sx={{bgcolor:t.color}}>{t.initials}</Avatar>}
              label={t.name.split(' ')[0]} variant={assignTech===t.id?'filled':'outlined'} color={assignTech===t.id?'primary':'default'}
              onClick={()=>setAssignTech(t.id)} sx={{cursor:'pointer'}}/>))}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={()=>setAssignOpen(false)}>Cancel</Button>
          <Button onClick={confirmAssign} variant="contained" disabled={!assignTech}>Assign & Schedule</Button></DialogActions>
      </Dialog>

      <Dialog open={detOpen} onClose={()=>setDetOpen(false)} maxWidth="sm" fullWidth>
        {detEv&&(<><DialogTitle sx={{display:'flex',alignItems:'center',gap:1}}>
          <Box sx={{width:12,height:12,borderRadius:'50%',bgcolor:detEv.techColor,flexShrink:0}}/>{detEv.title}
          <Box sx={{flex:1}}/><IconButton size="small" onClick={()=>setDetOpen(false)}><CloseIcon/></IconButton></DialogTitle>
          <DialogContent dividers><Box sx={{display:'flex',flexDirection:'column',gap:1.5}}>
            <Box sx={{display:'flex',gap:1,flexWrap:'wrap'}}>
              <Chip label={detEv.ticketNumber} size="small" variant="outlined"/>
              <Chip label={detEv.status.replace('_',' ')} size="small" sx={{bgcolor:statusColors[detEv.status],color:'white'}}/>
              {detEv.returnTrip && <Chip label="⚠ RETURN TRIP REQUIRED" size="small" sx={{bgcolor:'#fff7ed',color:'#c2410c',fontWeight:700,border:'1px solid #fdba74'}}/>}
              <Chip label={detEv.priority} size="small" sx={{bgcolor:priorityColors[detEv.priority],color:'white'}}/>
            </Box>
            <Typography variant="body2"><strong>Tech:</strong> {detEv.techName}</Typography>
            {detEv.customerName&&<Typography variant="body2"><strong>Customer:</strong> {detEv.customerName}</Typography>}
            {detEv.address&&<Typography variant="body2"><LocationIcon sx={{fontSize:14,mr:0.5,verticalAlign:'middle'}}/>{detEv.address}</Typography>}
            <Divider/>
            <Typography variant="body2"><AccessTimeIcon sx={{fontSize:14,mr:0.5,verticalAlign:'middle'}}/>
              <strong>Scheduled:</strong> {fmtTime(detEv.start)} – {fmtTime(detEv.end)} ({differenceInMinutes(detEv.end,detEv.start)} min)</Typography>
            {detEv.actualStart&&detEv.actualEnd&&(<Typography variant="body2" color="success.main">
              <DoneIcon sx={{fontSize:14,mr:0.5,verticalAlign:'middle'}}/><strong>Actual:</strong> {fmtTime(detEv.actualStart)} – {fmtTime(detEv.actualEnd)} ({differenceInMinutes(detEv.actualEnd,detEv.actualStart)} min)</Typography>)}
            {detEv.originalStart&&detEv.originalStart.getTime()!==detEv.start.getTime()&&(
              <Typography variant="body2" color="text.secondary"><strong>Originally scheduled:</strong> {fmtTime(detEv.originalStart)} (adjusted by system)</Typography>)}
            <Divider/>
            <FormControlLabel control={<Checkbox checked={detEv.pinnedStart} onChange={()=>{togglePin(detEv);setDetOpen(false);}}
              icon={<PinOutlinedIcon/>} checkedIcon={<PinIcon/>}/>}
              label={<Box><Typography variant="body2" fontWeight={600}>Do not adjust start time</Typography>
                <Typography variant="caption" color="text.secondary">{detEv.pinnedStart?'Locked — system will not auto-adjust. Only a person can change it.':'Enable to prevent the scheduling system from moving this appointment.'}</Typography></Box>}/>
          </Box></DialogContent>
          <DialogActions><Button onClick={()=>{setDetOpen(false);navigate(`/tickets?id=${detEv.id}`);}}>Open Ticket</Button>
            <Button onClick={()=>setDetOpen(false)} variant="contained">Close</Button></DialogActions></>)}
      </Dialog>

      <Snackbar open={!!snackMsg} autoHideDuration={4000} onClose={()=>setSnackMsg('')} anchorOrigin={{vertical:'bottom',horizontal:'center'}}>
        <Alert severity={snackSev} onClose={()=>setSnackMsg('')} variant="filled">{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}
